import { useState, useMemo } from 'react';
import { Loader2, AlertCircle, Cable, Search, History } from 'lucide-react';
import { Header } from '@/components/Header';
import { cn } from '@/lib/utils';
import { useCableSpools, useAllCableHistory } from './hooks/useCable';
import { CableHistorySheet } from './components/CableHistorySheet';
import { CableUsageSheet } from './components/CableUsageSheet';
import type { CableSpool, CableUsageHistoryItem } from '@/api/legacy';

function formatShortDate(dateStr: string | null): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = String(d.getFullYear()).slice(-2);
    return `${day}.${month}.${year}`;
  } catch {
    return '';
  }
}

function CableSpoolCard({
  spool,
  onClick,
  onShowHistory,
}: {
  spool: CableSpool;
  onClick: () => void;
  onShowHistory: () => void;
}) {
  const remaining = parseInt(spool.ostatok) || 0;
  const lastUsage = parseInt(spool.lastRashod || '') || 0;
  const lastCompany = spool.lastCompany || null;
  const lastDate = formatShortDate(spool.lastDate);

  const getStatusColor = () => {
    if (remaining <= 0) return 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700';
    if (remaining <= 50) return 'bg-red-100 dark:bg-red-950 border-red-200 dark:border-red-900';
    if (remaining <= 100) return 'bg-amber-100 dark:bg-amber-950 border-amber-200 dark:border-amber-900';
    return 'bg-card border-border';
  };

  return (
    <div
      className={cn(
        'p-3 rounded-lg border cursor-pointer transition-colors hover:bg-muted/50',
        getStatusColor(),
        spool.isMyOwn && 'ring-2 ring-primary ring-offset-1'
      )}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-bold text-lg">#{spool.name}</span>
          <span className={cn('text-sm', remaining <= 50 ? 'text-red-600 font-medium' : 'text-muted-foreground')}>
            {remaining} м
          </span>
          <span className="text-xs text-muted-foreground">• {spool.username}</span>
          {spool.isMyOwn && (
            <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
              Моя
            </span>
          )}
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onShowHistory();
          }}
          className="p-1.5 hover:bg-muted rounded-md transition-colors"
          title="История расхода"
        >
          <History className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>

      {(lastUsage > 0 || lastCompany) && (
        <div className="text-sm text-muted-foreground mt-1">
          Посл.: {lastCompany || '—'} ({lastUsage} м){lastDate && ` • ${lastDate}`}
        </div>
      )}
    </div>
  );
}

function HistoryItem({ item }: { item: CableUsageHistoryItem }) {
  const date = formatShortDate(item.created);
  const meters = parseInt(item.rashod) || 0;

  return (
    <div className="p-2.5 rounded-lg border bg-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium">#{item.buhtaName || '?'}</span>
          <span className="text-muted-foreground">{meters} м</span>
          <span className="text-muted-foreground">• {item.username}</span>
        </div>
        <span className="text-xs text-muted-foreground">{date}</span>
      </div>
      <div className="text-sm text-muted-foreground truncate">{item.company || '—'}</div>
    </div>
  );
}

export function CablePage() {
  const [activeTab, setActiveTab] = useState<'spools' | 'history'>('spools');

  // Spools tab state
  const { data: spools, isLoading: spoolsLoading, error: spoolsError } = useCableSpools();
  const [showOnlyMine, setShowOnlyMine] = useState(false);
  const [hideEmpty, setHideEmpty] = useState(true);
  const [spoolSearch, setSpoolSearch] = useState('');

  // History tab state
  const [historyOnlyMine, setHistoryOnlyMine] = useState(true);
  const [historySearch, setHistorySearch] = useState('');
  const { data: allHistory, isLoading: historyLoading, error: historyError } = useAllCableHistory(historyOnlyMine);

  // Sheet states
  const [historySheetOpen, setHistorySheetOpen] = useState(false);
  const [historySpoolId, setHistorySpoolId] = useState<string | null>(null);
  const [usageSheetOpen, setUsageSheetOpen] = useState(false);
  const [usageSpoolId, setUsageSpoolId] = useState<string | null>(null);

  // Filter spools
  const filteredSpools = useMemo(() => {
    if (!spools) return [];
    return spools.filter((s) => {
      if (showOnlyMine && !s.isMyOwn) return false;
      if (hideEmpty && parseInt(s.ostatok) <= 0) return false;
      if (spoolSearch) {
        const q = spoolSearch.toLowerCase();
        const matchNumber = s.name.toLowerCase().includes(q);
        const matchOwner = s.username?.toLowerCase().includes(q);
        if (!matchNumber && !matchOwner) return false;
      }
      return true;
    });
  }, [spools, showOnlyMine, hideEmpty, spoolSearch]);

  const sortedSpools = useMemo(() =>
    [...filteredSpools].sort((a, b) => parseInt(b.name) - parseInt(a.name)),
    [filteredSpools]
  );

  // Filter history
  const filteredHistory = useMemo(() => {
    if (!allHistory) return [];
    if (!historySearch) return allHistory;
    const q = historySearch.toLowerCase();
    return allHistory.filter((h) => {
      const matchClient = h.company?.toLowerCase().includes(q);
      const matchSpool = h.buhtaName?.toLowerCase().includes(q);
      const matchUser = h.username?.toLowerCase().includes(q);
      return matchClient || matchSpool || matchUser;
    });
  }, [allHistory, historySearch]);

  // Counts
  const mySpoolsCount = spools?.filter((s) => s.isMyOwn).length || 0;
  const nonEmptyCount = spools?.filter((s) => parseInt(s.ostatok) > 0).length || 0;

  const historySpool = spools?.find((s) => s.id === historySpoolId);
  const usageSpool = spools?.find((s) => s.id === usageSpoolId);

  const openHistory = (spoolId: string) => {
    setHistorySpoolId(spoolId);
    setHistorySheetOpen(true);
  };

  const openUsage = (spoolId: string) => {
    setUsageSpoolId(spoolId);
    setUsageSheetOpen(true);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header title="Кабель" showBack />

      {/* Tabs */}
      <div className="flex border-b">
        <button
          onClick={() => setActiveTab('spools')}
          className={cn(
            'flex-1 py-3 text-sm font-medium transition-colors',
            activeTab === 'spools'
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          <Cable className="w-4 h-4 inline mr-1.5" />
          Бухты
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={cn(
            'flex-1 py-3 text-sm font-medium transition-colors',
            activeTab === 'history'
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          <History className="w-4 h-4 inline mr-1.5" />
          История
        </button>
      </div>

      {/* Spools Tab */}
      {activeTab === 'spools' && (
        <main className="flex-1 p-4 overflow-y-auto">
          {/* Search */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Номер или владелец..."
              value={spoolSearch}
              onChange={(e) => setSpoolSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg bg-background focus:outline-hidden focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <button
              onClick={() => setHideEmpty(!hideEmpty)}
              className={cn(
                'text-sm px-3 py-1 rounded-full border transition-colors',
                hideEmpty
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-card border-border hover:bg-accent'
              )}
            >
              Непустые ({nonEmptyCount})
            </button>
            <button
              onClick={() => setShowOnlyMine(!showOnlyMine)}
              className={cn(
                'text-sm px-3 py-1 rounded-full border transition-colors',
                showOnlyMine
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-card border-border hover:bg-accent'
              )}
            >
              Мои ({mySpoolsCount})
            </button>
            <span className="text-xs text-muted-foreground ml-auto">
              {sortedSpools.length} из {spools?.length || 0}
            </span>
          </div>

          {/* Loading */}
          {spoolsLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          )}

          {/* Error */}
          {spoolsError && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="w-12 h-12 text-destructive mb-4" />
              <p className="text-muted-foreground">Ошибка загрузки</p>
            </div>
          )}

          {/* Empty */}
          {!spoolsLoading && !spoolsError && sortedSpools.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Cable className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {spoolSearch ? 'Ничего не найдено' : 'Нет бухт'}
              </p>
            </div>
          )}

          {/* Spools list */}
          {!spoolsLoading && !spoolsError && sortedSpools.length > 0 && (
            <div className="grid gap-3">
              {sortedSpools.map((spool) => (
                <CableSpoolCard
                  key={spool.id}
                  spool={spool}
                  onClick={() => openUsage(spool.id)}
                  onShowHistory={() => openHistory(spool.id)}
                />
              ))}
            </div>
          )}
        </main>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <main className="flex-1 p-4 overflow-y-auto">
          {/* Search */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Клиент, бухта или сотрудник..."
              value={historySearch}
              onChange={(e) => setHistorySearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg bg-background focus:outline-hidden focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Filter */}
          <div className="flex items-center gap-2 mb-4">
            <button
              onClick={() => setHistoryOnlyMine(!historyOnlyMine)}
              className={cn(
                'text-sm px-3 py-1 rounded-full border transition-colors',
                historyOnlyMine
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-card border-border hover:bg-accent'
              )}
            >
              Только мои
            </button>
            <span className="text-xs text-muted-foreground ml-auto">
              {filteredHistory.length} записей
            </span>
          </div>

          {/* Loading */}
          {historyLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          )}

          {/* Error */}
          {historyError && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="w-12 h-12 text-destructive mb-4" />
              <p className="text-muted-foreground">Ошибка загрузки</p>
            </div>
          )}

          {/* Empty */}
          {!historyLoading && !historyError && filteredHistory.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <History className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {historySearch ? 'Ничего не найдено' : 'Нет записей'}
              </p>
            </div>
          )}

          {/* History list */}
          {!historyLoading && !historyError && filteredHistory.length > 0 && (
            <div className="grid gap-3">
              {filteredHistory.map((item) => (
                <HistoryItem key={item.id} item={item} />
              ))}
            </div>
          )}
        </main>
      )}

      {/* History Sheet */}
      <CableHistorySheet
        open={historySheetOpen}
        onOpenChange={setHistorySheetOpen}
        spool={historySpool || null}
      />

      {/* Usage Sheet */}
      <CableUsageSheet
        open={usageSheetOpen}
        onOpenChange={setUsageSheetOpen}
        spool={usageSpool || null}
      />
    </div>
  );
}
